import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import HomeHero from "./home/HomeHero"
import HomeAudiences from "./home/HomeAudiences"
import HomeBuildingHistory from "./home/HomeBuildingHistory"
import HomeTestimonial from "./home/HomeTestimonial"
import HomeLocations from "./home/HomeLocations"
import type { ArticleLink } from "@/components/ResidentTherapistCard"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import { absoluteUrl } from "@/lib/absolute-url"
import type { GiselaTherapist, PageMeta, PageProps } from "@/types/inertia"

interface SiteCard {
  id: number
  name: string
  slug: string
  region: string
  address: string
  maps_url: string | null
  features: string[]
  primary_photo_url: string | null
  href: string
  gallery_href: string
}

interface HeroSlide {
  photo_url: string
  /** 1200×630 JPEG for og:image — link-preview scrapers often refuse WebP. */
  social_url?: string | null
  alt_text: string | null
}

interface Props {
  therapist: GiselaTherapist | null
  latest_articles: ArticleLink[]
  testimonial: { quote: string; attribution: string } | null
  sites: SiteCard[]
  hero_slides: HeroSlide[]
  /** The film over the hero slides — its section's display setting. */
  hero_film?: string | null
  content: {
    hero: { eyebrow: string; subtext: string; therapy_cta: string; room_cta: string; _hidden?: boolean }
    audiences: {
      clients_label: string
      clients_heading: string
      clients_body: string
      clients_cta: string
      therapists_label: string
      therapists_heading: string
      therapists_body: string
      therapists_cta: string
      _hidden?: boolean
    }
    building_history: { title: string; p1: string; p2: string; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

export default function Home({ therapist, latest_articles, testimonial, sites, hero_slides, hero_film, content, section_order, meta }: Props) {
  const { brand_urls } = usePage<PageProps>().props
  const title = meta?.title ?? "Sanctuary Therapy Centre — Prestwich & Hebden Bridge"
  const description =
    meta?.description ??
    "Two calm therapy centres in Prestwich, Manchester and Hebden Bridge: CBT and EMDR therapy with Gisela Ramirez, and rooms for practitioners to hire by the hour."

  // Every block below the hero, named by its registry entry so the admin's
  // order applies. These blocks each carry their own background, so the
  // index is unused here.
  const bands = presentBands(
    [
      { id: "home-audiences", present: !sectionHidden(content.audiences),
        render: () => (
          <HomeAudiences
            therapist={therapist}
            heroSlide={hero_slides?.[0] ?? null}
            articles={latest_articles}
            content={content.audiences}
          />
        ) },
      { id: "home-locations", present: true, render: () => <HomeLocations sites={sites} /> },
      { id: "home-building-history", present: !sectionHidden(content.building_history),
        render: () => <HomeBuildingHistory content={content.building_history} /> },
      { id: "home-testimonial", present: true, render: () => <HomeTestimonial testimonial={testimonial} /> },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            meta?.description ??
            "Two calm therapy centres in Prestwich, Manchester and Hebden Bridge: CBT and EMDR therapy with Gisela Ramirez, and rooms for practitioners to hire by the hour."
          }
        />
        {/* Social previews want an absolute URL; the slide's is a path. */}
        {hero_slides?.[0] && <meta property="og:image" content={absoluteUrl(hero_slides[0].social_url ?? hero_slides[0].photo_url, brand_urls.sanctuary)} />}
        {hero_slides?.[0] && <meta name="twitter:card" content="summary_large_image" />}
        {hero_slides?.[0] && (
          <link rel="preload" as="image" href={hero_slides[0].photo_url} fetchPriority="high" />
        )}
      </Head>
      <PublicLayout>
        {!sectionHidden(content.hero) && <HomeHero slides={hero_slides} sites={sites} content={content.hero} film={hero_film} />}
        {/* The hero's two location cards only exist from lg up, so on a phone
            the addresses disappear from the top of the page altogether. Flex
            ordering floats Our locations up to just under the hero there and
            leaves the desktop order — the audience fork first — untouched. */}
        <div className="flex flex-col">
          {bands.map((band) => (
            <div key={band.id} className={band.id === "home-locations" ? "max-lg:order-first" : undefined}>
              {band.render(0)}
            </div>
          ))}
        </div>
      </PublicLayout>
    </>
  )
}
